import React, { useCallback, useMemo, useRef, useState } from "react";
import Plotly from "plotly.js-basic-dist";
import Plot from "./PlotlyChart";
import LegendControls from "./LegendControls";
import {
  getWeightLengthSeriesColor,
  sortSpeciesForWeightLengthLegend,
} from "../../graphView/colors";
import {
  buildLengthTicks,
  buildWrYTicksForDomain,
  getWrYAxisFromValues,
} from "./axisTicks";
import { plotlyAxisBase, plotlyAxisTitle } from "./plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "./plotlyLegendLayout";
import { useGraphV2PlotHeight } from "../ChartFullscreenShell";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import {
  isWrReferenceTraceName,
  wrHundredLegendTraceNumeric,
} from "./plotlyReferenceLineLegend";
import { WrLabel, LABEL } from "../../../../utils/samplingDisplayLabels";
import "./wrVsLengthPlotly.css";

const CARD_W = 268;
const CARD_H = 190;

export default function WrVsLengthChart({
  graphData,
  selectedUnit,
  selectedFish,
  isMobile,
}) {
  const wrapRef = useRef(null);
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);
  const [tip, setTip] = useState(null);

  const orderedSpecies = useMemo(() => {
    const unique = [...new Set(graphData.map((d) => d.species))];
    return sortSpeciesForWeightLengthLegend(unique);
  }, [graphData]);

  const lengthUnit = selectedUnit.value === "English" ? "Inch" : "mm";
  const lengthTitle = `Length (${selectedUnit.value === "English" ? "Inch" : "mm"})`;

  const xTicks = useMemo(() => {
    return buildLengthTicks(
      graphData.map((d) => Number(d.length || 0)),
      selectedUnit.value
    );
  }, [graphData, selectedUnit.value]);

  const traces = useMemo(() => {
    const t = [];
    let xMin = xTicks[0] ?? 0;
    let xMax = xTicks[xTicks.length - 1] ?? 0;
    if (graphData.length) {
      const lens = graphData.map((d) => Number(d.length || 0)).filter(Number.isFinite);
      if (lens.length) {
        xMin = Math.min(xMin, ...lens);
        xMax = Math.max(xMax, ...lens);
      }
    }
    if (xMax < xMin) {
      [xMin, xMax] = [xMax, xMin];
    }
    const standardWrLegendTrace = wrHundredLegendTraceNumeric(xMin, xMax);
    orderedSpecies.forEach((species) => {
      const rows = graphData.filter((d) => d.species === species);
      if (!rows.length) return;
      const colorOrObj = getWeightLengthSeriesColor(species, orderedSpecies);
      const seriesColor =
        typeof colorOrObj === "string" ? colorOrObj : colorOrObj.color;
      const seriesShape =
        typeof colorOrObj === "string" ? "circle" : colorOrObj.shape;
      t.push({
        type: "scatter",
        mode: "markers",
        name: `${species}`,
        x: rows.map((r) => r.length),
        y: rows.map((r) => r.relativeWeight),
        marker: {
          color: seriesColor,
          size: 9,
          opacity: 0.85,
          symbol: seriesShape,
          line: { color: "white", width: 1 },
        },
        customdata: rows.map((r) => [
          r.species,
          r.isStocking ? "stocking" : "sample",
        ]),
        hovertemplate: "<extra></extra>",
      });
    });
    if (standardWrLegendTrace) t.push(standardWrLegendTrace);
    return t;
  }, [graphData, orderedSpecies, xTicks]);

  const legendLayout = useMemo(
    () => plotlyHorizontalLegendForTraces(traces, isMobile),
    [traces, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: 500,
    desktopDefault: 700,
    legendExtra: legendLayout.extraHeight || 0,
  });

  const wrYAxis = useMemo(() => {
    const base = getWrYAxisFromValues(
      graphData.map((d) => Number(d.relativeWeight || 0))
    );
    const yMin = Math.min(base.yMin, 1);
    const yMax = Math.max(base.yMax, 1);
    if (yMin === base.yMin && yMax === base.yMax) return base;
    return {
      yMin,
      yMax,
      yTicks: buildWrYTicksForDomain(yMin, yMax),
    };
  }, [graphData]);

  const xAxisLayout = useMemo(
    () => ({
      title: plotlyAxisTitle(lengthTitle, isMobile),
      zeroline: false,
      tickmode: "array",
      tickvals: xTicks,
      range: [xTicks[0] || 0, xTicks[xTicks.length - 1] || 0],
      tickangle: isMobile ? -45 : 0,
      ...plotlyAxisBase(isMobile),
    }),
    [lengthTitle, isMobile, xTicks]
  );

  const yAxisLayout = useMemo(
    () => ({
      title: plotlyAxisTitle(LABEL.WR_PCT_HTML, isMobile),
      tickmode: "array",
      tickvals: wrYAxis.yTicks,
      range: [wrYAxis.yMin, wrYAxis.yMax],
      tickformat: ".0%",
      ...plotlyAxisBase(isMobile),
    }),
    [isMobile, wrYAxis]
  );

  const onHover = useCallback(
    (eventData) => {
      const pt = eventData?.points?.[0];
      const el = wrapRef.current;
      const evt = eventData?.event;
      if (!pt || el == null || typeof pt.x !== "number" || typeof pt.y !== "number") {
        return;
      }
      let cx;
      let cy;
      if (evt && typeof evt.clientX === "number" && typeof evt.clientY === "number") {
        cx = evt.clientX;
        cy = evt.clientY;
      } else if (pt.bbox) {
        cx = (pt.bbox.x0 + pt.bbox.x1) / 2;
        cy = pt.bbox.y0;
      } else {
        return;
      }
      const cd = pt.customdata;
      const species = Array.isArray(cd)
        ? String(cd[0] ?? "")
        : String(cd ?? "");
      const isStocking = Array.isArray(cd) && cd[1] === "stocking";
      const length = pt.x;
      const wr = pt.y;
      const rect = el.getBoundingClientRect();
      const pad = 12;
      let left = cx - rect.left + pad;
      let top = cy - rect.top + pad;
      if (left + CARD_W > rect.width - 8) {
        left = cx - rect.left - CARD_W - pad;
      }
      if (top + CARD_H > rect.height - 8) {
        top = cy - rect.top - CARD_H - pad;
      }
      left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
      top = Math.max(8, Math.min(top, rect.height - CARD_H - 8));
      setTip({
        left,
        top,
        species: species || "",
        length,
        wr,
        aboveStandard: wr >= 1,
        isStocking,
      });
    },
    []
  );

  const onUnhover = useCallback(() => setTip(null), []);

  const handleSelectAll = useCallback(() => {
    if (plotRef.current && plotRef.current.el) {
      const update = { visible: traces.map(() => true) };
      const indices = traces.map((_, i) => i);
      Plotly.restyle(plotRef.current.el, update, indices);
    }
  }, [traces]);

  const handleDeselectAll = useCallback(() => {
    if (plotRef.current && plotRef.current.el) {
      const update = {
        visible: traces.map((tr) =>
          isWrReferenceTraceName(tr.name) ? true : "legendonly"
        ),
      };
      const indices = traces.map((_, i) => i);
      Plotly.restyle(plotRef.current.el, update, indices);
    }
  }, [traces]);

  return (
    <div
      ref={wrapRef}
      className="wr-vs-length-plotly-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <LegendControls
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
      />
      <Plot
        ref={plotRef}
        data={traces}
        layout={{
          autosize: true,
          height: plotHeight,
          margin: { t: legendLayout.marginTop, r: 40, b: isMobile ? 110 : 90, l: 70 },
          paper_bgcolor: "white",
          plot_bgcolor: "white",
          xaxis: xAxisLayout,
          yaxis: yAxisLayout,
          legend: legendLayout.legend,
          hovermode: "closest",
          hoverdistance: 10,
        }}
        config={{ responsive: true, displaylogo: false }}
        style={{ width: "100%" }}
        useResizeHandler
        onHover={onHover}
        onUnhover={onUnhover}
      />
      {tip && (
        <div
          className={`wr-length-hover-card${isMobile ? " wr-length-hover-card--mobile" : ""}`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title">
              {tip.species}
              {tip.isStocking ? " · Stocked" : ""}
            </p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Length:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--length">
                {tip.length.toFixed(2)} {lengthUnit}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">
                <WrLabel />:
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--wr">
                {(tip.wr * 100).toFixed(1)}%
              </span>
            </div>
          </div>
          <div
            className={
              tip.aboveStandard
                ? "wr-length-hover-card__badge wr-length-hover-card__badge--ok"
                : "wr-length-hover-card__badge wr-length-hover-card__badge--low"
            }
          >
            {tip.aboveStandard
              ? "✓ Fish is at or above standard weight"
              : "Fish is below standard weight"}
          </div>
        </div>
      )}
    </div>
  );
}
